import {
  Alert01Icon,
  ArrowRight01Icon,
  BarcodeScanIcon,
  Camera01Icon,
  CheckListIcon,
  Clock01Icon,
  PackageReceiveIcon,
  Ticket01Icon,
  WifiIcon,
  WifiOffIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMobileContext } from '@/contexts/mobile-context';
import { authClient } from '@/lib/better-auth';
import { maintenanceTicketsApi, gamificationApi } from '@/lib/api-client';

export function MyWorkPage() {
  const { t } = useTranslation();
  const { isOnline, hasPendingSync, pendingSyncCount } = useMobileContext();
  const { data: session } = authClient.useSession();
  const userId = session?.user?.id;

  // Fetch tickets assigned to current user
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['my-work', 'tickets', userId],
    queryFn: () =>
      maintenanceTicketsApi.getAll({
        assignedTo: userId,
        status: 'open,in_progress',
        limit: 20,
        sort: 'createdAt',
        order: 'desc',
      }),
    enabled: !!userId,
  });

  // Fetch pending training approvals
  const { data: trainingsData, isLoading: trainingsLoading } = useQuery({
    queryKey: ['my-work', 'trainings', userId],
    queryFn: () =>
      gamificationApi.getUserTrainings(`status=pending-approval&userId=${userId}&limit=5`),
    enabled: !!userId,
  });

  const tickets = (ticketsData as any)?.data ?? [];
  const pendingApprovals = (trainingsData as any) ?? [];

  // Split tickets: overdue vs today
  const now = new Date();
  const overdue = tickets.filter((t: any) => {
    if (!t.scheduledAt && !t.dueDate) return false;
    const targetDate = new Date(t.scheduledAt || t.dueDate);
    return targetDate < now;
  });
  const todayTasks = tickets.filter((t: any) => {
    if (!t.scheduledAt && !t.dueDate) return true;
    const targetDate = new Date(t.scheduledAt || t.dueDate);
    const today = new Date();
    return targetDate.toDateString() === today.toDateString();
  });
  const upcoming = tickets.filter((t: any) => {
    if (!t.scheduledAt && !t.dueDate) return false;
    return !overdue.includes(t) && !todayTasks.includes(t);
  });

  const priorityColor: Record<string, string> = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-blue-100 text-blue-800',
    low: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="flex flex-col gap-4 pb-20">
      {/* Status Bar */}
      <div className="flex items-center justify-between px-1">
        <h1 className="text-xl font-bold tracking-tight">{t('mobile.myWork')}</h1>
        <div className="flex items-center gap-2">
          {!isOnline && (
            <Badge variant="destructive" className="gap-1">
              <HugeiconsIcon icon={WifiOffIcon} className="h-3 w-3" />
              Offline
            </Badge>
          )}
          {hasPendingSync && (
            <Badge variant="secondary" className="gap-1">
              <HugeiconsIcon icon={WifiIcon} className="h-3 w-3" />
              {pendingSyncCount} pending
            </Badge>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-2">
        <Link
          to="/tickets"
          className="flex flex-col items-center gap-1 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <HugeiconsIcon icon={Ticket01Icon} className="h-6 w-6 text-primary" />
          <span className="text-[10px] font-medium">Tickets</span>
        </Link>
        <Link
          to="/tickets"
          className="flex flex-col items-center gap-1 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <HugeiconsIcon icon={Camera01Icon} className="h-6 w-6 text-primary" />
          <span className="text-[10px] font-medium">Photo</span>
        </Link>
        <a
          href="/assets"
          className="flex flex-col items-center gap-1 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <HugeiconsIcon icon={BarcodeScanIcon} className="h-6 w-6 text-primary" />
          <span className="text-[10px] font-medium">Scan</span>
        </a>
        <Link
          to="/parts-management"
          search={{ tab: 'items' } as any}
          className="flex flex-col items-center gap-1 p-3 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors"
        >
          <HugeiconsIcon icon={PackageReceiveIcon} className="h-6 w-6 text-primary" />
          <span className="text-[10px] font-medium">Parts</span>
        </Link>
      </div>

      {/* Overdue Alert */}
      {overdue.length > 0 && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-950/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <HugeiconsIcon icon={Alert01Icon} className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-800 dark:text-red-400">
                  {overdue.length} overdue ticket{overdue.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600 dark:text-red-500">
                  {overdue[0].title} — {overdue[0].assetId}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Tasks */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <HugeiconsIcon icon={Clock01Icon} className="h-4 w-4" />
            My Tasks
          </CardTitle>
          <CardDescription>
            {ticketsLoading ? 'Loading...' : `${tickets.length} assigned`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {ticketsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-16 w-full rounded-lg" />
            </div>
          ) : tickets.length === 0 ? (
            <p className="text-muted-foreground text-center py-4 text-sm">
              No assigned tickets. Great job! 🎉
            </p>
          ) : (
            [...todayTasks, ...upcoming, ...overdue].slice(0, 10).map((ticket: any) => (
              <Link
                key={ticket.id}
                to="/tickets/$ticketId"
                params={{ ticketId: ticket.id } as any}
                className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
              >
                <div className="flex-shrink-0">
                  <HugeiconsIcon icon={Ticket01Icon} className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{ticket.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {ticket.assetName || ticket.assetId || '—'}
                    {ticket.scheduledAt
                      ? ` · ${new Date(ticket.scheduledAt).toLocaleDateString()}`
                      : ''}
                  </p>
                </div>
                {ticket.priority && (
                  <Badge
                    className={priorityColor[ticket.priority] ?? 'bg-gray-100'}
                    variant="outline"
                  >
                    {ticket.priority}
                  </Badge>
                )}
                <HugeiconsIcon icon={ArrowRight01Icon} className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))
          )}
        </CardContent>
      </Card>

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <HugeiconsIcon icon={CheckListIcon} className="h-4 w-4" />
              Pending Approvals
            </CardTitle>
            <CardDescription>{pendingApprovals.length} waiting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {trainingsLoading ? (
              <Skeleton className="h-12 w-full rounded-lg" />
            ) : (
              pendingApprovals.map((item: any) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="min-w-0 flex-1 mr-2">
                    <p className="font-medium text-sm truncate">
                      {item.training?.name ?? item.name ?? 'Training'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {item.userName ?? item.user?.name ?? item.userId}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs">
                      Review
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Offline Banner */}
      {!isOnline && (
        <div className="fixed bottom-16 left-0 right-0 bg-yellow-500 text-yellow-900 p-3 text-center text-sm font-medium z-50">
          You are offline. Changes will sync when connection is restored.
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/my-work')({
  component: MyWorkPage,
});
